import { useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'

function isUser(
	user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Action = 'create' | 'read' | 'update' | 'delete'
type Entity = 'user' | 'content' | 'role' | 'permission'
type Access = 'own' | 'any' | 'own,any' | 'any,own'
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

export function userHasPermission(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some((role) =>
		role.permissions.some(
			(permission) =>
				permission.entity === entity &&
				permission.action === action &&
				(!access || access.includes(permission.access)),
		),
	)
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}

/**
 * Check if user has admin or moderator (editor) role.
 */
export function userIsEditorOrAbove(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
): boolean {
	if (!user) return false
	return userHasRole(user, 'admin') || userHasRole(user, 'moderator')
}

/**
 * Client-side check for per-content-type permissions.
 * Checks for specific entity (content.LORE) then falls back to generic (content).
 */
export function userHasContentPermission(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
	action: 'create' | 'read' | 'update' | 'delete',
	contentType: string,
	access?: 'own' | 'any',
): boolean {
	if (!user) return false
	const specificEntity = `content.${contentType}`
	return user.roles.some((role) =>
		role.permissions.some((p) => {
			const actionMatch = p.action === action
			const entityMatch = p.entity === specificEntity || p.entity === 'content'
			const accessMatch = !access || access === p.access
			return actionMatch && entityMatch && accessMatch
		}),
	)
}
